import React from "react";
import CodeMirror from "@uiw/react-codemirror";
import { Play, Trash2, Sun, Moon, FileText, CheckCircle, XCircle, AlertTriangle, Shield } from "lucide-react";
import { Button, CircularProgress, Tabs, Tab, Typography, TextField, Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { oneDark, defaultLightThemeOption } from "@uiw/react-codemirror";
import Tooltip from "@mui/material/Tooltip";
import ProctoringWarningModal from "../proctoring/ProctoringWarningModal";
import ReadyForCodingAssignment from "./ReadyForCodingAssignment";

const ProctoredCodeEditor = ({
    isDarkMode,
    proctoringContainerRef,
    // Start screen
    showProctoringStart,
    handleStartProctoredSession,
    // Submit confirm dialog
    showSubmitConfirm,
    setShowSubmitConfirm,
    handleSubmitCode,
    // Violation modal
    activeViolation,
    totalViolations,
    handleResumeAfterViolation,
    // Submission state
    isSubmitted,
    showCompilerAfterSubmit,
    setShowCompilerAfterSubmit,
    // Timer
    timeLeft,
    formatTime,
    // Proctoring data for results view
    proctoringLogs,
    pasteEvents,
    // Content & language
    activeContent,
    activeLanguage,
    problemStatement,
    // Editor state
    activeFile,
    loading,
    isCodeEmpty,
    handleRunCode,
    handleClearCode,
    toggleTheme,
    codeMirrorRef,
    extensions,
    handleCodeChange,
    // Tabs / IO
    activeTab,
    setActiveTab,
    testResults,
    executionResult,
    output,
    error,
    userInput,
    setUserInput,
}) => {

    // ── Styles ──────────────────────────────────────────────────────────────────
    const createButtonStyle = (bgColor) => ({
        fontWeight: 600, borderRadius: "10px",
        width: "50px", height: "50px", minWidth: "50px", minHeight: "50px",
        padding: "0", margin: "0", border: "none",
        boxShadow: "0 3px 6px rgba(0,0,0,0.2)", cursor: "pointer",
        transition: "all 0.2s ease-in-out",
        display: "flex", alignItems: "center", justifyContent: "center",
        position: "relative", overflow: "hidden",
        background: bgColor, color: "white",
        "&:hover": { transform: "translateY(-2px) scale(1.05)", boxShadow: "0 6px 12px rgba(0,0,0,0.3)" },
        "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
    });

    const executeButtonStyles = createButtonStyle(
        isDarkMode ? "linear-gradient(135deg,#10b981,#059669)" : "linear-gradient(135deg,#22c55e,#16a34a)"
    );
    const clearButtonStyles = createButtonStyle(
        isDarkMode ? "linear-gradient(135deg,#ef4444,#dc2626)" : "linear-gradient(135deg,#f87171,#ef4444)"
    );
    const themeToggleButtonStyles = createButtonStyle(
        isDarkMode ? "linear-gradient(135deg,#f59e0b,#d97706)" : "linear-gradient(135deg,#8b5cf6,#7c3aed)"
    );

    const controlsContainerStyles = {
        background: isDarkMode ? "linear-gradient(90deg,#252544,#252544)" : "linear-gradient(90deg,#e0e7ff,#c3daff)",
        borderBottom: isDarkMode ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.1)",
        padding: "8px 16px", height: "60px", boxSizing: "border-box",
        display: "flex", alignItems: "center", gap: "16px", flexShrink: 0,
    };

    const editorHeaderStyles = {
        height: "30px", fontSize: "14px", fontWeight: 600, padding: "8px 16px",
        color: isDarkMode ? "white" : "#333",
        background: isDarkMode ? "linear-gradient(90deg,#252544,#252544)" : "linear-gradient(90deg,#e0e7ff,#c3daff)",
        borderBottom: isDarkMode ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.1)",
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
    };

    const codeMirrorStyles = {
        flex: 1, borderBottomLeftRadius: "8px", borderBottomRightRadius: "8px",
        background: isDarkMode ? "#1f2937" : "#ffffff",
        color: isDarkMode ? "#e2e8f0" : "#1f2937",
        width: "100%", boxSizing: "border-box", overflow: "hidden", minWidth: 0,
    };

    const tabsContainerStyles = {
        background: isDarkMode ? "linear-gradient(90deg,#252544,#252544)" : "linear-gradient(90deg,#e0e7ff,#c3daff)",
        borderBottom: isDarkMode ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.1)",
        height: "40px", boxSizing: "border-box", flexShrink: 0,
    };

    const tabStyles = {
        color: isDarkMode ? "#e5e7eb" : "#374151", fontSize: "0.85rem", fontWeight: 600,
        textTransform: "none", padding: "10px 16px", height: "40px", minHeight: "40px",
        "&.Mui-selected": { color: isDarkMode ? "#93c5fd" : "#1e40af", fontWeight: 700 },
    };

    const contentAreaStyles = {
        flex: 1, padding: "0.75rem", overflowY: "auto",
        background: isDarkMode ? "#1f2937" : "#ffffff", boxSizing: "border-box", minWidth: 0,
    };

    const accordionStyles = {
        background: isDarkMode ? "#252544" : "#f8fafc", borderRadius: "6px",
        marginBottom: "0.5rem", boxShadow: isDarkMode ? "0 2px 6px rgba(0,0,0,0.3)" : "0 2px 6px rgba(0,0,0,0.1)",
        "&:before": { display: "none" },
    };

    const accordionSummaryStyles = {
        background: isDarkMode ? "linear-gradient(90deg,#252544,#252544)" : "linear-gradient(90deg,#e0e7ff,#c3daff)",
        borderRadius: "6px", padding: "0 0.75rem", minHeight: "36px", height: "36px",
        "& .MuiAccordionSummary-content": { margin: "0", display: "flex", alignItems: "center" },
    };

    const accordionDetailsStyles = {
        padding: "0.75rem", background: isDarkMode ? "#1f2937" : "#ffffff",
        borderRadius: "0 0 6px 6px", boxSizing: "border-box", maxHeight: "150px", overflowY: "auto",
    };

    const cellStyles = {
        fontFamily: '"Fira Code", monospace', fontSize: "0.85rem",
        color: isDarkMode ? "#e5e7eb" : "#1f2937", lineHeight: "1.4",
        padding: "0.5rem", border: isDarkMode ? "1px solid #4b5568" : "1px solid #d1d5db",
        borderRadius: "4px", marginBottom: "0.25rem", wordBreak: "break-word",
    };

    const labelStyles = { fontWeight: 600, color: isDarkMode ? "#93c5fd" : "#1e40af", marginRight: "0.5rem" };

    const resultRowStyles = {
        padding: "0.5rem", background: isDarkMode ? "#252544" : "#f9fafb",
        borderRadius: "4px", border: isDarkMode ? "1px solid rgba(255,255,255,0.1)" : "1px solid #e5e7eb",
        marginBottom: "0.5rem", boxSizing: "border-box",
    };

    const resultHeaderStyles = {
        fontSize: "0.9rem", fontWeight: 600, color: isDarkMode ? "#e5e7eb" : "#1f2937",
        paddingBottom: "0.25rem", borderBottom: isDarkMode ? "1px solid rgba(255,255,255,0.1)" : "1px solid #e5e7eb",
        display: "flex", alignItems: "center", gap: "8px",
    };

    const outputContentStyles = {
        background: isDarkMode ? "#18181b" : "#f3f4f6", padding: "1rem", borderRadius: "4px",
        border: isDarkMode ? "1px solid #4b5568" : "1px solid #d1d5db",
        fontFamily: '"Fira Code", monospace', whiteSpace: "pre-wrap", wordBreak: "break-word",
        color: isDarkMode ? "#e5e7eb" : "#1f2937", fontSize: "0.9rem", lineHeight: "1.5",
        height: "100%", overflowY: "auto", boxSizing: "border-box", minHeight: "200px",
    };

    const outputPreStyles = {
        margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word",
        color: error ? "#ef4444" : isDarkMode ? "#e5e7eb" : "#1f2937",
        fontSize: "0.9rem", lineHeight: "1.5",
    };

    const customInputStyles = {
        width: "100%", height: "100%", boxSizing: "border-box",
        "& .MuiOutlinedInput-root": {
            height: "100%", fontFamily: '"Fira Code", monospace', fontSize: "0.85rem",
            alignItems: "flex-start",
            "& fieldset": { border: isDarkMode ? "1px solid #4b5568" : "1px solid #d1d5db", borderRadius: "4px" },
        },
        "& .MuiOutlinedInput-input": {
            color: isDarkMode ? "#e5e7eb" : "#1f2937", background: isDarkMode ? "#18181b" : "#f3f4f6",
            borderRadius: "4px", padding: "0.75rem", height: "100% !important", minHeight: "200px",
        },
    };

    const emptyStateStyles = {
        textAlign: "center", height: "100%", color: isDarkMode ? "#9ca3af" : "#6b7280",
        padding: "1.5rem", fontStyle: "italic", fontSize: "0.9rem",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "0.5rem",
    };

    // ── Shared I/O tabs (used in both LeetCode layout and can be reused) ────────
    const renderIOTabs = () => (
        <>
            <div style={tabsContainerStyles}>
                <Tabs
                    value={activeTab}
                    onChange={(_, v) => setActiveTab(v)}
                    TabIndicatorProps={{ style: { backgroundColor: isDarkMode ? "#93c5fd" : "#1e40af", height: "3px", borderRadius: "3px" } }}
                    sx={{ minHeight: "40px" }}
                >
                    <Tab label="Test Cases" sx={tabStyles} />
                    <Tab label={`Test Results (${testResults.filter(r => r.passed).length}/${testResults.length})`} sx={tabStyles} />
                    <Tab label="Custom Input" sx={tabStyles} />
                    <Tab label="Output" sx={tabStyles} />
                </Tabs>
            </div>
            <div style={contentAreaStyles}>
                {activeTab === 0 && (
                    <div>
                        {activeContent?.testCases?.length > 0 ? (
                            activeContent.testCases.map((test, i) => (
                                <Accordion key={i} style={accordionStyles}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon style={{ color: isDarkMode ? "#e5e7eb" : "#1f2937" }} />} style={accordionSummaryStyles}>
                                        <Typography style={{ fontWeight: 600, color: isDarkMode ? "#93c5fd" : "#1e40af", fontSize: "0.9rem" }}>
                                            Test Case {i + 1}
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails style={accordionDetailsStyles}>
                                        <div style={cellStyles}><span style={labelStyles}>Input:</span>{test.testInput || "[Empty]"}</div>
                                        <div style={cellStyles}><span style={labelStyles}>Expected Output:</span>{test.expectedOutput || "[Empty]"}</div>
                                    </AccordionDetails>
                                </Accordion>
                            ))
                        ) : (
                            <div style={emptyStateStyles}><FileText size={36} /><div>No test cases available.</div></div>
                        )}
                    </div>
                )}
                {activeTab === 1 && (
                    <div>
                        {testResults.length > 0 ? (
                            testResults.map((result) => (
                                <Accordion key={result.index} style={{ ...accordionStyles, border: `2px solid ${result.passed ? "#10b981" : result.error ? "#f59e0b" : "#ef4444"}` }}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon style={{ color: isDarkMode ? "#e5e7eb" : "#1f2937" }} />} style={accordionSummaryStyles}>
                                        <div style={resultHeaderStyles}>
                                            {result.passed ? <CheckCircle color="#10b981" size={18} /> : result.error ? <AlertTriangle color="#f59e0b" size={18} /> : <XCircle color="#ef4444" size={18} />}
                                            <span>Test {result.index}: {result.passed ? "Passed" : result.error ? "Error" : "Failed"}</span>
                                        </div>
                                    </AccordionSummary>
                                    <AccordionDetails style={accordionDetailsStyles}>
                                        <div style={resultRowStyles}>
                                            <div style={cellStyles}><span style={labelStyles}>Input:</span>{result.input || "[Empty]"}</div>
                                            <div style={cellStyles}><span style={labelStyles}>Expected:</span>{result.expected}</div>
                                            <div style={cellStyles}>
                                                <span style={labelStyles}>{result.error ? "Error:" : "Actual Output:"}</span>
                                                <span style={{ color: result.error ? "#f59e0b" : result.passed ? "#10b981" : "#ef4444" }}>{result.got}</span>
                                            </div>
                                        </div>
                                    </AccordionDetails>
                                </Accordion>
                            ))
                        ) : executionResult ? (
                            <div style={resultRowStyles}>
                                <div style={resultHeaderStyles}><span>Execution Result</span></div>
                                {executionResult.stdout && <div style={cellStyles}><span style={labelStyles}>Output:</span>{executionResult.stdout}</div>}
                                {executionResult.stderr && <div style={cellStyles}><span style={labelStyles}>Error:</span><span style={{ color: "#f59e0b" }}>{executionResult.stderr}</span></div>}
                                {executionResult.error && <div style={cellStyles}><span style={labelStyles}>Error:</span><span style={{ color: "#ef4444" }}>{executionResult.error}</span></div>}
                            </div>
                        ) : (
                            <div style={emptyStateStyles}><FileText size={36} /><div>No test results available.</div></div>
                        )}
                    </div>
                )}
                {activeTab === 2 && (
                    <div style={{ height: "100%", boxSizing: "border-box" }}>
                        <TextField placeholder="Enter input for stdin..." onChange={(e) => setUserInput(e.target.value)}
                            multiline rows={5} value={userInput} variant="outlined" sx={customInputStyles} />
                    </div>
                )}
                {activeTab === 3 && (
                    <div style={outputContentStyles}>
                        <pre style={outputPreStyles}>{output || error || "No output to display."}</pre>
                    </div>
                )}
            </div>
        </>
    );

    // ── Render ───────────────────────────────────────────────────────────────────
    return (
        <>
            {/* ── Proctoring start screen ──────────────────────────────────────── */}
            {showProctoringStart && (
                <div style={{
                    position: "absolute", inset: 0, zIndex: 200,
                    background: isDarkMode ? "#f1f5f9" : "#f1f5f9",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    padding: "2rem", borderRadius: "8px", overflowY: "auto",
                }}>
                    <ReadyForCodingAssignment
                        startHandler={handleStartProctoredSession}
                        language={activeContent?.language}
                        testCasesCount={activeContent?.testCases?.length || 0}
                        timerMinutes={activeContent?.timerMinutes}
                        isProctored={true}
                        contentType="code"
                    />
                </div>
            )}

            {/* ── Submit confirmation overlay ──────────────────────────────────── */}
            {showSubmitConfirm && (
                <div style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.75)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <div style={{
                        background: isDarkMode ? "#1f2937" : "#fff", borderRadius: "16px", padding: "2rem 2.5rem",
                        maxWidth: "430px", width: "90%", textAlign: "center",
                        border: isDarkMode ? "1px solid #4a5568" : "1px solid #e5e7eb",
                        boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
                    }}>
                        <div style={{ fontSize: "2.5rem", marginBottom: "0.75rem" }}>⚠️</div>
                        <h3 style={{ color: isDarkMode ? "#f9fafb" : "#111827", fontWeight: 700, marginBottom: "0.75rem", fontSize: "1.2rem" }}>
                            Submit Your Solution?
                        </h3>
                        <p style={{ color: isDarkMode ? "#9ca3af" : "#6b7280", lineHeight: 1.65, marginBottom: "1.75rem", fontSize: "0.95rem" }}>
                            This will <strong>permanently submit</strong> your code and end the proctored session.
                            Your proctoring report and all code snapshots will be saved.
                        </p>
                        <div style={{ display: "flex", gap: "1rem", justifyContent: "center" }}>
                            <button onClick={() => setShowSubmitConfirm(false)} style={{
                                background: "transparent", border: isDarkMode ? "1px solid #4a5568" : "1px solid #d1d5db",
                                color: isDarkMode ? "#d1d5db" : "#374151", borderRadius: "10px",
                                padding: "0.6rem 1.5rem", fontWeight: 600, cursor: "pointer", fontSize: "0.95rem",
                            }}>Cancel</button>
                            <button onClick={() => { setShowSubmitConfirm(false); handleSubmitCode("manual"); }} style={{
                                background: "linear-gradient(135deg,#10b981,#059669)", border: "none", color: "#fff",
                                borderRadius: "10px", padding: "0.6rem 1.75rem", fontWeight: 700,
                                cursor: "pointer", fontSize: "0.95rem", boxShadow: "0 4px 12px rgba(16,185,129,0.4)",
                            }}>Yes, Submit</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Violation modal ──────────────────────────────────────────────── */}
            {!showProctoringStart && !isSubmitted && (
                <ProctoringWarningModal
                    show={!!activeViolation}
                    violationType={activeViolation}
                    violationCount={totalViolations}
                    onResume={handleResumeAfterViolation}
                    container={proctoringContainerRef}
                />
            )}

            {/* ── Results view (after submission) ─────────────────────────────── */}
            {isSubmitted && !showCompilerAfterSubmit && (() => {
                const submittedCode = activeContent?.codeSnippet || activeFile?.content || "";
                const submittedAt = activeContent?.submittedAt?.$date || activeContent?.submittedAt;
                const submitReason = activeContent?.submitReason || "manual";
                const pd = activeContent?.codeProctoringData;
                const violations = pd?.violations || proctoringLogs || [];
                const pasteCount = pd?.pasteEvents?.length ?? pasteEvents.length;
                return (
                    <div style={{ height: "100%", overflowY: "auto", display: "flex", flexDirection: "column", background: isDarkMode ? "#1a202c" : "#f0f2f5" }}>
                        <div style={{ background: "linear-gradient(135deg,#10b981,#059669)", color: "#fff", padding: "1.25rem 2rem", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
                            <div>
                                <h3 style={{ margin: 0, fontWeight: 700, fontSize: "1.2rem" }}>✅ Solution Submitted</h3>
                                <div style={{ fontSize: "0.85rem", opacity: 0.9, marginTop: "4px" }}>
                                    {submitReason === "timer" ? "Auto-submitted (time expired)" : "Manually submitted"}
                                    {submittedAt && ` · ${new Date(submittedAt).toLocaleString()}`}
                                </div>
                            </div>
                            <button onClick={() => setShowCompilerAfterSubmit(true)} style={{
                                background: "#fff", color: "#059669", border: "none", borderRadius: "10px",
                                padding: "0.55rem 1.4rem", fontWeight: 700, fontSize: "0.9rem", cursor: "pointer",
                            }}>
                                🔄 Run in Compiler
                            </button>
                        </div>
                        <div style={{ display: "flex", flex: 1, gap: "1.25rem", padding: "1.25rem", overflow: "hidden", minHeight: 0 }}>
                            <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
                                <h4 style={{ color: isDarkMode ? "#93c5fd" : "#1e40af", fontWeight: 700, marginBottom: "0.6rem", fontSize: "0.95rem" }}>
                                    Submitted Code — {activeLanguage.name}
                                </h4>
                                <div style={{ flex: 1, borderRadius: "8px", overflow: "hidden", border: isDarkMode ? "1px solid #4a5568" : "1px solid #e5e7eb" }}>
                                    <CodeMirror value={submittedCode} height="100%"
                                        extensions={[activeLanguage.extension()]}
                                        theme={isDarkMode ? oneDark : defaultLightThemeOption}
                                        readOnly style={{ height: "100%" }} />
                                </div>
                            </div>
                            <div style={{ width: "270px", flexShrink: 0, display: "flex", flexDirection: "column", gap: "1rem" }}>
                                {/* Details */}
                                <div style={{ background: isDarkMode ? "#1f2937" : "#fff", borderRadius: "12px", padding: "1.1rem", border: isDarkMode ? "1px solid #4a5568" : "1px solid #e5e7eb" }}>
                                    <h4 style={{ color: isDarkMode ? "#f9fafb" : "#111827", fontWeight: 700, marginBottom: "0.5rem", fontSize: "0.95rem" }}>📝 Details</h4>
                                    <div style={{ color: isDarkMode ? "#9ca3af" : "#6b7280", fontSize: "0.84rem", lineHeight: 1.8 }}>
                                        <div><strong>Language:</strong> {activeLanguage.name}</div>
                                        <div><strong>Test Cases:</strong> {activeContent?.testCases?.length || 0}</div>
                                        <div><strong>Submit:</strong> {submitReason === "timer" ? "Timer expired" : "Manual"}</div>
                                        {submittedAt && <div><strong>Time:</strong> {new Date(submittedAt).toLocaleTimeString()}</div>}
                                    </div>
                                </div>

                                {/* Teacher review — score */}
                                {activeContent?.teacher_reviewed ? (() => {
                                    const earned = activeContent.earnedMarks ?? 0;
                                    const total = activeContent.totalMarks ?? (activeContent.marksPerTestCase * (activeContent.testCases?.length || 0));
                                    const pct = total > 0 ? Math.round((earned / total) * 100) : 0;
                                    const scoreColor = pct >= 80 ? "#10b981" : pct >= 50 ? "#f59e0b" : "#ef4444";
                                    return (
                                        <div style={{ background: isDarkMode ? "#1f2937" : "#fff", borderRadius: "12px", padding: "1.1rem", border: `1px solid ${scoreColor}40` }}>
                                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.75rem" }}>
                                                <h4 style={{ color: isDarkMode ? "#f9fafb" : "#111827", fontWeight: 700, margin: 0, fontSize: "0.95rem" }}>⭐ Your Score</h4>
                                                <span style={{ background: "#d1fae5", color: "#065f46", fontSize: "0.68rem", fontWeight: 700, padding: "2px 8px", borderRadius: "20px" }}>REVIEWED</span>
                                            </div>
                                            <div style={{ textAlign: "center", padding: "0.25rem 0 0.5rem" }}>
                                                <div style={{ fontSize: "2.8rem", fontWeight: 800, color: scoreColor, lineHeight: 1 }}>{earned}</div>
                                                <div style={{ color: isDarkMode ? "#9ca3af" : "#6b7280", fontSize: "0.82rem", marginTop: "2px" }}>out of {total} marks</div>
                                            </div>
                                            <div style={{ background: isDarkMode ? "#374151" : "#f3f4f6", borderRadius: "8px", height: "8px", overflow: "hidden", margin: "0.5rem 0" }}>
                                                <div style={{ width: `${pct}%`, height: "100%", background: scoreColor, borderRadius: "8px", transition: "width 0.6s ease" }} />
                                            </div>
                                            <div style={{ textAlign: "center", fontSize: "0.78rem", color: scoreColor, fontWeight: 700 }}>{pct}%</div>
                                        </div>
                                    );
                                })() : (
                                    <div style={{ background: isDarkMode ? "#1f2937" : "#fffbeb", borderRadius: "12px", padding: "1rem", border: "1px solid #fcd34d", textAlign: "center" }}>
                                        <div style={{ fontSize: "1.4rem", marginBottom: "4px" }}>⏳</div>
                                        <div style={{ fontWeight: 700, fontSize: "0.85rem", color: isDarkMode ? "#fcd34d" : "#92400e" }}>Pending Review</div>
                                        <div style={{ fontSize: "0.75rem", color: isDarkMode ? "#9ca3af" : "#6b7280", marginTop: "4px" }}>Your instructor will review and assign marks</div>
                                    </div>
                                )}

                                {/* Teacher feedback */}
                                {activeContent?.teacher_reviewed && activeContent?.teacherFeedback && (
                                    <div style={{ background: isDarkMode ? "#1f2937" : "#eff6ff", borderRadius: "12px", padding: "1.1rem", border: "1px solid #bfdbfe" }}>
                                        <h4 style={{ color: isDarkMode ? "#93c5fd" : "#1e40af", fontWeight: 700, marginBottom: "0.5rem", fontSize: "0.95rem" }}>💬 Teacher Feedback</h4>
                                        <p style={{ color: isDarkMode ? "#d1d5db" : "#374151", fontSize: "0.85rem", lineHeight: 1.65, margin: 0, whiteSpace: "pre-wrap" }}>
                                            {activeContent.teacherFeedback}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* ── LeetCode 3-panel layout (live session + compiler-after-submit) ─ */}
            {((!showProctoringStart && !isSubmitted) || showCompilerAfterSubmit) && (
                    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
                        {/* Toolbar */}
                        <div style={{ ...controlsContainerStyles, justifyContent: "flex-start" }}>
                            {!showCompilerAfterSubmit && (
                                <div style={{
                                    display: "flex", alignItems: "center", gap: "6px",
                                    background: totalViolations > 0 ? "rgba(245,158,11,0.15)" : "rgba(16,185,129,0.15)",
                                    border: `1px solid ${totalViolations > 0 ? "#f59e0b" : "#10b981"}`,
                                    borderRadius: "8px", padding: "4px 10px",
                                    color: totalViolations > 0 ? "#f59e0b" : "#10b981",
                                    fontSize: "0.78rem", fontWeight: 700, flexShrink: 0,
                                }}>
                                    <Shield size={14} />
                                    {totalViolations > 0 ? `Warnings: ${totalViolations}` : "Proctored"}
                                </div>
                            )}
                            {showCompilerAfterSubmit && (
                                <button onClick={() => setShowCompilerAfterSubmit(false)} style={{
                                    background: "linear-gradient(135deg,#6366f1,#4f46e5)", color: "#fff",
                                    border: "none", borderRadius: "10px", padding: "0 16px", height: "50px",
                                    fontWeight: 700, fontSize: "0.9rem", cursor: "pointer", flexShrink: 0,
                                    boxShadow: "0 3px 6px rgba(0,0,0,0.2)",
                                }}>← Back to Results</button>
                            )}
                            <Tooltip title="Execute code" arrow>
                                <span>
                                    <Button sx={executeButtonStyles} onClick={handleRunCode} disabled={loading || isCodeEmpty || (isSubmitted && !showCompilerAfterSubmit)}>
                                        {loading ? <CircularProgress size={24} color="inherit" /> : <Play size={24} />}
                                    </Button>
                                </span>
                            </Tooltip>
                            <Tooltip title="Clear" arrow>
                                <span>
                                    <Button sx={clearButtonStyles} onClick={handleClearCode} disabled={loading || isCodeEmpty || (isSubmitted && !showCompilerAfterSubmit)}>
                                        <Trash2 size={24} />
                                    </Button>
                                </span>
                            </Tooltip>
                            <Tooltip title="Toggle Theme" arrow>
                                <Button sx={themeToggleButtonStyles} onClick={toggleTheme} disabled={loading}>
                                    {isDarkMode ? <Sun size={24} /> : <Moon size={24} />}
                                </Button>
                            </Tooltip>
                            {!showCompilerAfterSubmit && (
                                <button
                                    onClick={() => !isSubmitted && setShowSubmitConfirm(true)}
                                    disabled={loading || isSubmitted}
                                    style={{
                                        background: isSubmitted ? "linear-gradient(135deg,#6b7280,#4b5563)" : "linear-gradient(135deg,#10b981,#059669)",
                                        color: "#fff", border: "none", borderRadius: "10px",
                                        padding: "0 18px", height: "50px", fontWeight: 700,
                                        fontSize: "0.9rem", cursor: isSubmitted ? "not-allowed" : "pointer",
                                        flexShrink: 0, boxShadow: "0 3px 6px rgba(0,0,0,0.2)",
                                        opacity: isSubmitted || loading ? 0.6 : 1,
                                    }}
                                >{isSubmitted ? "✓ Submitted" : "Submit"}</button>
                            )}
                            {timeLeft !== null && !showCompilerAfterSubmit && (
                                <div style={{
                                    marginLeft: "auto",
                                    background: timeLeft <= 60 ? "rgba(239,68,68,0.15)" : timeLeft <= 300 ? "rgba(245,158,11,0.15)" : "rgba(16,185,129,0.12)",
                                    border: `1px solid ${timeLeft <= 60 ? "#ef4444" : timeLeft <= 300 ? "#f59e0b" : "#10b981"}`,
                                    borderRadius: "8px", padding: "4px 12px",
                                    color: timeLeft <= 60 ? "#ef4444" : timeLeft <= 300 ? "#f59e0b" : "#10b981",
                                    fontSize: "1rem", fontWeight: 700, fontVariantNumeric: "tabular-nums", letterSpacing: "0.05em",
                                }}>⏱ {formatTime(timeLeft)}</div>
                            )}
                            <div style={{ marginLeft: (timeLeft !== null && !showCompilerAfterSubmit) ? "8px" : "auto", color: isDarkMode ? "#9ca3af" : "#6b7280", fontSize: "0.85rem", fontWeight: 600, paddingRight: "8px" }}>
                                {activeLanguage.name}
                            </div>
                        </div>

                        {/* Main row: problem (left) | editor + I/O (right) */}
                        <div style={{ display: "flex", flex: 1, overflow: "hidden", minHeight: 0 }}>
                            {/* Problem statement panel */}
                            <div style={{
                                width: "35%", flexShrink: 0, overflowY: "auto",
                                padding: "1.25rem 1.5rem",
                                borderRight: isDarkMode ? "1px solid #4a5568" : "1px solid #e5e7eb",
                                background: isDarkMode ? "#1a202c" : "#ffffff",
                            }}>
                                <h3 style={{ color: isDarkMode ? "#93c5fd" : "#1976d2", fontWeight: 700, marginBottom: "0.75rem", fontSize: "1.05rem" }}>
                                    Solve the following problem using {activeLanguage.name} language.
                                </h3>
                                <p style={{ color: isDarkMode ? "#d1d5db" : "#374151", fontSize: "0.95rem", lineHeight: "1.75", margin: 0, whiteSpace: "pre-line" }}>
                                    {problemStatement || "No problem statement provided."}
                                </p>
                            </div>

                            {/* Editor + I/O panel */}
                            <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minWidth: 0 }}>
                                <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minHeight: 0 }}>
                                    <div style={editorHeaderStyles}>Code Editor ({activeLanguage.name})</div>
                                    <CodeMirror
                                        ref={codeMirrorRef}
                                        value={activeFile.content}
                                        height="100%"
                                        extensions={extensions}
                                        onChange={handleCodeChange}
                                        theme={isDarkMode ? oneDark : defaultLightThemeOption}
                                        style={{ ...codeMirrorStyles, flex: 1 }}
                                    />
                                </div>
                                <div style={{ height: "290px", flexShrink: 0, borderTop: isDarkMode ? "2px solid #4a5568" : "2px solid #e5e7eb", display: "flex", flexDirection: "column", overflow: "hidden" }}>
                                    {renderIOTabs()}
                                </div>
                            </div>
                        </div>
                    </div>
            )}
        </>
    );
};

export default ProctoredCodeEditor;
